import { useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';

/**
 * Campo "Parcelas de pagamento".
 *
 * Nasceu no fluxo de Obras: o pagamento a fornecedor raramente é de uma vez só,
 * e cada obra combina um jeito ("50% entrada / 50% no fim", "3 parcelas"...).
 * O tipo "progresso" não servia porque as tarefas dele são fixas na
 * configuração — aqui a lista é **do card**: quem cria define as parcelas na
 * hora, e o financeiro vai marcando o que foi pago.
 *
 * Regras combinadas com o André (11/09/2026):
 *   - cada parcela tem nome e valor opcional;
 *   - quando é marcada como paga, a data e quem marcou ficam gravados sozinhos;
 *   - mexer na lista (adicionar, renomear, remover) é de quem criou o card ou
 *     de admin — no financeiro a pessoa só marca como paga. A lista é o
 *     combinado com o fornecedor; não deve mudar no meio do caminho.
 *
 * O valor gravado é uma lista de objetos. Cards antigos do tipo "progresso"
 * guardavam só os nomes marcados (["50% Entrada"]); isso é lido e convertido
 * aqui mesmo, sem precisar migrar antes.
 */

export interface Parcela {
  id: string;
  nome: string;
  valor: number | null;
  paga: boolean;
  pagaEm: string | null;
  pagaPor: string | null;
  pagaPorNome: string | null;
}

const novoId = () => `parc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const dinheiro = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** 1500.5 -> "1.500,5" — o jeito que a pessoa digitaria */
const paraEntrada = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/**
 * Texto digitado -> número.
 *
 * "1.500,50" -> 1500.5 · "R$ 2.000" -> 2000 · "1.500" -> 1500 · "1500.5" -> 1500.5
 *
 * O caso que engana é "1.500" sem vírgula: lido como decimal viraria R$ 1,50.
 * Aqui, pontos em grupos de três dígitos são separador de milhar — é assim que
 * se digita dinheiro no Brasil.
 */
export const lerValor = (texto: any): number | null => {
  if (typeof texto === 'number') return Number.isFinite(texto) ? texto : null;
  const limpo = String(texto ?? '').replace(/[^\d,.-]/g, '');
  if (!/\d/.test(limpo)) return null;
  let normal: string;
  if (limpo.includes(',')) {
    normal = limpo.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(limpo)) {
    normal = limpo.replace(/\./g, '');
  } else {
    normal = limpo;
  }
  const n = Number(normal);
  return Number.isFinite(n) ? n : null;
};

/**
 * Qualquer formato que o campo já teve vira a lista de parcelas.
 * - lista de objetos: o formato novo;
 * - lista de textos: o antigo "progresso" (textos = o que estava marcado);
 * - vazio: parte das parcelas padrão da configuração, nenhuma paga.
 */
export const normalizarParcelas = (valor: any, padrao: string[] = []): Parcela[] => {
  const completar = (p: any): Parcela => ({
    id: p.id || novoId(),
    nome: String(p.nome ?? ''),
    valor: lerValor(p.valor),
    paga: !!p.paga,
    pagaEm: p.pagaEm || null,
    pagaPor: p.pagaPor || null,
    pagaPorNome: p.pagaPorNome || null,
  });

  if (Array.isArray(valor) && valor.length > 0 && typeof valor[0] === 'object' && valor[0] !== null) {
    return valor.map(completar);
  }
  if (Array.isArray(valor) && valor.every((v) => typeof v === 'string')) {
    const marcados = new Set(valor as string[]);
    const nomes = [...padrao, ...(valor as string[]).filter((v) => !padrao.includes(v))];
    return nomes.map((nome, i) => completar({ id: `parc_legado_${i}`, nome, paga: marcados.has(nome) }));
  }
  return padrao.map((nome, i) => completar({ id: `parc_padrao_${i}`, nome, paga: false }));
};

/** Resumo em texto, para listas, histórico e busca. */
export const textoParcelas = (valor: any): string => {
  const lista = normalizarParcelas(valor);
  if (lista.length === 0) return '';
  const pagas = lista.filter((p) => p.paga).length;
  return `${pagas}/${lista.length} pagas — ` + lista.map((p) => `${p.nome}${p.paga ? ' ✓' : ''}`).join(', ');
};

interface Props {
  valor: any;
  opcoesPadrao?: string[];
  onChange: (novo: Parcela[]) => void;
  podeEditarLista: boolean;
  podeMarcarPaga: boolean;
  usuario?: { id?: string; nome?: string } | null;
  /** grava as parcelas padrão logo ao abrir — só faz sentido no formulário de criação */
  semearPadrao?: boolean;
}

const CampoParcelas = ({
  valor, opcoesPadrao = [], onChange, podeEditarLista, podeMarcarPaga, usuario, semearPadrao,
}: Props) => {
  const parcelas = useMemo(() => normalizarParcelas(valor, opcoesPadrao), [valor, opcoesPadrao]);
  const [novoNome, setNovoNome] = useState('');
  const [novoValor, setNovoValor] = useState('');
  const semeou = useRef(false);

  // No formulário de criação, as parcelas padrão precisam existir no card mesmo
  // que ninguém mexa nelas — senão o financeiro abre o card e não acha nada.
  useEffect(() => {
    if (!semearPadrao || semeou.current) return;
    semeou.current = true;
    if (!Array.isArray(valor) && opcoesPadrao.length > 0) onChange(parcelas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trocar = (id: string, mudanca: Partial<Parcela>) =>
    onChange(parcelas.map((p) => (p.id === id ? { ...p, ...mudanca } : p)));

  const alternarPaga = (p: Parcela) => {
    if (!podeMarcarPaga) return;
    trocar(p.id, p.paga
      ? { paga: false, pagaEm: null, pagaPor: null, pagaPorNome: null }
      : { paga: true, pagaEm: new Date().toISOString(), pagaPor: usuario?.id || null,
          pagaPorNome: usuario?.nome || null });
  };

  const adicionar = () => {
    const nome = novoNome.trim();
    if (!nome) return;
    onChange([...parcelas, {
      id: novoId(), nome, valor: lerValor(novoValor),
      paga: false, pagaEm: null, pagaPor: null, pagaPorNome: null,
    }]);
    setNovoNome('');
    setNovoValor('');
  };

  const total = parcelas.length;
  const pagas = parcelas.filter((p) => p.paga).length;
  const comValor = parcelas.some((p) => p.valor !== null);
  const valorTotal = parcelas.reduce((s, p) => s + (p.valor || 0), 0);
  const valorPago = parcelas.filter((p) => p.paga).reduce((s, p) => s + (p.valor || 0), 0);
  const percentual = total ? Math.round((pagas / total) * 100) : 0;

  return (
    <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100 space-y-1.5">
      {total === 0 && (
        <p className="text-xs text-gray-400 px-1">
          {podeEditarLista ? 'Nenhuma parcela ainda. Adicione abaixo.' : 'Nenhuma parcela definida.'}
        </p>
      )}

      {parcelas.map((p) => (
        <div key={p.id}
          className={`flex items-center gap-2 px-1.5 py-1 rounded-md ${p.paga ? 'bg-emerald-50/70' : 'hover:bg-gray-100'}`}>
          <button type="button" onClick={() => alternarPaga(p)} disabled={!podeMarcarPaga}
            title={podeMarcarPaga ? (p.paga ? 'desmarcar pagamento' : 'marcar como paga') : ''}
            className={`h-4 w-4 flex-shrink-0 rounded border flex items-center justify-center transition-colors
              ${p.paga ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-300'}
              ${podeMarcarPaga ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
            {p.paga && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
          </button>

          <div className="flex-1 min-w-0">
            {podeEditarLista ? (
              <input value={p.nome} onChange={(e) => trocar(p.id, { nome: e.target.value })}
                className="w-full bg-transparent text-xs text-gray-800 border-0 border-b border-transparent focus:border-gray-300 focus:outline-none px-0 py-0.5" />
            ) : (
              <span className={`text-xs ${p.paga ? 'text-gray-600' : 'text-gray-800'}`}>{p.nome}</span>
            )}
            {p.paga && (p.pagaEm || p.pagaPorNome) && (
              <p className="text-[10px] text-emerald-700 leading-tight">
                paga{p.pagaEm ? ` em ${new Date(p.pagaEm).toLocaleDateString('pt-BR')}` : ''}
                {p.pagaPorNome ? ` · ${p.pagaPorNome}` : ''}
              </p>
            )}
          </div>

          {podeEditarLista ? (
            // Valor só é convertido ao sair do campo: convertendo a cada tecla, a
            // vírgula sumia assim que era digitada e não dava para pôr centavos.
            <input key={`${p.id}-${p.valor ?? ''}`}
              defaultValue={p.valor === null ? '' : paraEntrada(p.valor)}
              onBlur={(e) => {
                const novo = lerValor(e.target.value);
                if (novo !== p.valor) trocar(p.id, { valor: novo });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
              }}
              placeholder="R$ (opcional)" inputMode="decimal"
              className="w-24 text-right bg-white text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-gray-400" />
          ) : (
            p.valor !== null && <span className="text-xs text-gray-700 tabular-nums">{dinheiro(p.valor)}</span>
          )}

          {podeEditarLista && (
            <button type="button" onClick={() => onChange(parcelas.filter((x) => x.id !== p.id))}
              title="remover parcela" className="text-gray-300 hover:text-red-500 flex-shrink-0">
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}

      {podeEditarLista && (
        <div className="flex items-center gap-2 pt-1">
          <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar(); } }}
            placeholder="Nova parcela (ex.: 3ª parcela)"
            className="flex-1 min-w-0 bg-white text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-gray-400" />
          <input value={novoValor} onChange={(e) => setNovoValor(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar(); } }}
            placeholder="R$" inputMode="decimal"
            className="w-20 bg-white text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-gray-400" />
          <button type="button" onClick={adicionar} disabled={!novoNome.trim()}
            className="px-2 py-1 bg-gray-800 text-white rounded text-xs flex items-center gap-1 disabled:opacity-40">
            <PlusIcon className="h-3 w-3" /> Adicionar
          </button>
        </div>
      )}

      {total > 0 && (
        <div className="pt-1 px-1">
          <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
            <span>{pagas} de {total} paga(s) · {percentual}%</span>
            {comValor && <span className="tabular-nums">{dinheiro(valorPago)} de {dinheiro(valorTotal)}</span>}
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${percentual}%` }} />
          </div>
        </div>
      )}

      {!podeEditarLista && podeMarcarPaga && total > 0 && (
        <p className="text-[10px] text-gray-400 px-1">
          Aqui só se marca o pagamento. Mudar as parcelas é com quem criou o card ou um admin.
        </p>
      )}
    </div>
  );
};

export default CampoParcelas;
